import { Component } from '@theme/component';
import { bindDeferredVideo } from '@theme/echt-deferred-video';

/**
 * Echt · Collection promo tile.
 *
 * The tile is rendered by `sections/echt-collection-promo.liquid`, which sits
 * after `main` in the collection template. This component moves that one `<li>`
 * into the stock product grid and keeps it at its configured position.
 *
 * Why a MutationObserver and not an event: the theme publishes none for this.
 * `assets/events.js` declares only `media:started-playing`,
 * `quantity-selector:update`, `megaMenu:hover` and `zoom-media:selected`, and
 * `assets/results-list.js` dispatches nothing at all. The observer covers both
 * ways the grid changes — infinite scroll appending cards, and filtering
 * morphing the whole list through Section Rendering — with one mechanism.
 *
 * It observes the grid's *section*, not the grid itself, because a morph can
 * replace the `<ul>` and an observer bound to the old node would go deaf.
 *
 * Position is re-asserted only when the tile is missing or in the wrong place,
 * so the mutation our own insertion causes ends the loop instead of feeding it.
 *
 * No refs are used: `Component` resolves refs by querying inside itself, and
 * this component's entire job is to move its child somewhere else.
 */

const GRID_SELECTOR = 'ul.product-grid';
const SECTION_SELECTOR = '[id^="shopify-section-"]';

class EchtCollectionPromoComponent extends Component {
  /** @type {HTMLElement | null} */
  #tile = null;

  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  #stopVideo;
  #mediaObserver;
  #referenceMedia;

  #queued = false;

  connectedCallback() {
    super.connectedCallback();

    this.#tile = this.querySelector('.echt-collection-promo');
    if (!this.#tile) return;

    this.#video = this.#tile.querySelector('video');
    this.#place();
    if (this.#video) this.#stopVideo = bindDeferredVideo(this.#video, { rootMargin: '250px' });
    this.#watch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopVideo?.();
    this.#mediaObserver?.disconnect();
    this.#referenceMedia = null;

    // The tile lives outside this element, so nothing else would collect it.
    // Without this, a theme-editor re-render leaves the old tile in the grid
    // and the new one inserts beside it.
    this.#tile?.remove();
  }

  /** 1-based grid cell the tile should occupy. */
  get #position() {
    const value = Number.parseInt(this.dataset.position ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /* ------------------------------------------------------------ placement */

  /** The grid's owning section — stable across a Section Rendering morph. */
  #scope() {
    const anchor = document.querySelector(GRID_SELECTOR) ?? document.getElementById('ResultsList');
    return anchor?.closest(SECTION_SELECTOR) ?? anchor;
  }

  #watch() {
    const scope = this.#scope();
    // No grid on this page: the tile simply never appears. Nothing throws.
    if (!scope) return;

    this.#observer = new MutationObserver(this.#schedule);
    this.#observer.observe(scope, { childList: true, subtree: true });
  }

  // Batched to a microtask so a morph that touches a hundred nodes costs one
  // placement check, not a hundred.
  #schedule = () => {
    if (this.#queued) return;
    this.#queued = true;
    queueMicrotask(() => {
      this.#queued = false;
      if (this.#place()) this.#stopVideo?.refresh();
    });
  };

  /** @returns {boolean} whether the tile was actually moved. */
  #place() {
    const tile = this.#tile;
    const grid = document.querySelector(GRID_SELECTOR);
    if (!tile || !grid) return false;

    // `grid.children` also holds the inline <script> the stock snippet renders
    // as the list's first child, so cards are counted by tag, not by index.
    const cards = Array.from(grid.children).filter((el) => el.tagName === 'LI' && el !== tile);
    const index = Math.min(this.#position - 1, cards.length);
    const before = cards[index] ?? null;
    this.#matchMediaHeight((before ?? cards.at(-1))?.querySelector('.card-gallery'));

    // Already right: return before touching the DOM. This is what stops the
    // observer from re-firing on our own work.
    if (tile.parentElement === grid && tile.nextElementSibling === before) return false;

    grid.insertBefore(tile, before);
    return true;
  }

  // Include the product's title, promotion and price below its image.
  // Measure content endpoints rather than the stretched grid cell itself.
  #matchMediaHeight(media) {
    if (media === this.#referenceMedia) return;
    this.#mediaObserver?.disconnect();
    this.#referenceMedia = media;
    if (!media) {
      this.#tile?.style.removeProperty('--echt-promo-media-height');
      return;
    }
    const card = media.closest('product-card');
    const price = card?.querySelector('product-price');
    const update = () => {
      const imageBounds = media.getBoundingClientRect();
      const height = (price?.getBoundingClientRect().bottom ?? imageBounds.bottom) - imageBounds.top;
      if (height > 0) this.#tile?.style.setProperty('--echt-promo-media-height', `${height}px`);
    };
    this.#mediaObserver = new ResizeObserver(update);
    this.#mediaObserver.observe(media);
    if (price) this.#mediaObserver.observe(price);
    const content = card?.querySelector('.product-card__content');
    if (content) this.#mediaObserver.observe(content);
    update();
  }
}

if (!customElements.get('echt-collection-promo-component')) {
  customElements.define('echt-collection-promo-component', EchtCollectionPromoComponent);
}
